interface PricingSectionProps {
  onCTAClick: () => void;
}

const plans = [
  {
    id: "outbound",
    tier: "Tier 1",
    name: "Outbound Engine",
    nameAccent: "Your Data",
    tagline: "Designed to stay top of mind.",
    badge: null,
    price: "$12,500",
    pricePer: "/ month",
    description:
      "Leverage your existing relationships and stay top of mind with consistent outreach.",
    highlight: false,
    features: [
      "Upload your agent and loan officer database",
      "AI-powered outbound calling",
      "Consistent follow-up and visibility",
      "Upload Non-Directing Client Agent lists",
    ],
    roi: "Typical ROI: 5 – 7 closings / month",
    note: "Best for teams with strong existing relationships",
    cta: "Check Market Availability",
    ctaVariant: "primary",
  },
  {
    id: "triggered",
    tier: "Tier 2",
    name: "Market-Triggered Outreach",
    nameAccent: null,
    tagline: null,
    badge: "Most Popular",
    price: "$14,500 – $16,500",
    pricePer: "/ month",
    subHeading: "Engage agents when closings are happening",
    description:
      "Combine your database with real-time market activity to engage at the exact moment deals are happening.",
    highlight: true,
    features: [
      "Everything in Outbound Engine",
      "Real-time listing triggers",
      "Price decrease / pending deal triggers",
      "Agent activity signals",
      "Call agents when transactions are actually happening",
    ],
    roi: "Typical ROI: 7 – 9 closings / month",
    note: "Most partners choose this for consistent deal flow and timing advantage",
    cta: "Check Market Availability",
    ctaVariant: "primary",
  },
  {
    id: "expansion",
    tier: "Tier 3",
    name: "Market Dominance",
    nameAccent: "Multi Territory",
    tagline: null,
    badge: null,
    price: "Starting at $25,000+",
    pricePer: "/ month",
    description:
      "Designed for title companies looking to control multiple markets or entire states.",
    highlight: false,
    features: [
      "Multi-market and regional coverage",
      "Expanded data access",
      "Higher volume of agent engagement",
      "Strategic positioning across your territory",
    ],
    roi: null,
    note: null,
    cta: "Apply for Market Dominance",
    ctaVariant: "outline",
  },
];

const triggers = [
  { icon: "ri-home-4-line", label: "A listing goes live" },
  { icon: "ri-time-line", label: "A deal goes pending or list price lowered" },
  { icon: "ri-exchange-dollar-line", label: "A new transaction is created" },
];

export default function PricingSection({ onCTAClick }: PricingSectionProps) {
  return (
    <section id="pricing" className="bg-[#0B2A3A] py-14 md:py-24 px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">

        {/* Header */}
        <div className="text-center max-w-2xl mx-auto mb-10 md:mb-16">
          <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-1.5 mb-6">
            <i className="ri-price-tag-3-line text-[#F1663A] text-xs" />
            <span className="text-xs font-semibold text-white/80 tracking-wide uppercase">Investment</span>
          </div>
          <h2
            className="text-white font-bold mb-4"
            style={{ fontSize: "clamp(28px, 3.2vw, 44px)", lineHeight: "1.15" }}
          >
            Own Your Market
          </h2>
          <p className="text-white/70 text-base font-medium" style={{ lineHeight: "1.7" }}>
            Only <span className="text-[#F1663A] font-bold">ONE</span> title partner per market.{" "}
            Once your market is claimed, it&apos;s closed.
          </p>
        </div>

        {/* ROI Callout Banner */}
        <div className="max-w-6xl mx-auto mb-10">
          <div className="flex items-center justify-center gap-3 bg-white/5 border border-white/12 rounded-2xl px-6 py-4">
            <span className="w-8 h-8 flex items-center justify-center flex-shrink-0">
              <i className="ri-line-chart-line text-[#F1663A] text-lg" />
            </span>
            <p className="text-white/80 text-sm font-medium text-center leading-relaxed">
              Most partners cover their investment with{" "}
              <span className="text-white font-bold">8–10 additional closings per month</span>{" "}
              — everything beyond that is incremental revenue.
            </p>
          </div>
        </div>

        {/* Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 max-w-6xl mx-auto">
          {plans.map((plan) => (
            <div
              key={plan.id}
              className={`rounded-2xl flex flex-col relative overflow-hidden transition-transform hover:-translate-y-1 duration-300 ${
                plan.highlight
                  ? "bg-white ring-2 ring-[#F1663A]"
                  : "border border-white/15"
              }`}
              style={!plan.highlight ? { backgroundColor: "rgba(255,255,255,0.05)" } : {}}
            >

              {/* Most Popular badge */}
              {plan.badge && (
                <div className="absolute top-0 left-0 right-0 flex justify-center">
                  <span className="bg-[#F1663A] text-white text-xs font-bold px-5 py-1.5 rounded-b-xl tracking-wide uppercase">
                    {plan.badge}
                  </span>
                </div>
              )}

              <div className={`p-5 md:p-8 flex flex-col flex-1 ${plan.badge ? "pt-9 md:pt-12" : ""}`}>

                {/* Tier label */}
                <span className={`text-xs font-bold tracking-widest uppercase mb-2 ${plan.highlight ? "text-[#F1663A]" : "text-[#F1663A]/80"}`}>
                  {plan.tier}
                </span>

                {/* Plan name */}
                <h3 className={`text-lg font-bold mb-1 leading-tight ${plan.highlight ? "text-[#0B2A3A]" : "text-white"}`}>
                  {plan.name}
                  {plan.nameAccent && (
                    <span className={`block text-sm font-semibold mt-0.5 ${plan.highlight ? "text-[#F1663A]" : "text-[#F1663A]/80"}`}>
                      ({plan.nameAccent})
                    </span>
                  )}
                </h3>

                {/* Tagline (Tier 1 only) */}
                {plan.tagline && (
                  <p className={`text-xs font-semibold italic mb-1 ${plan.highlight ? "text-[#F1663A]" : "text-[#F1663A]/80"}`}>
                    {plan.tagline}
                  </p>
                )}

                {/* Sub-heading (Tier 2 only) */}
                {(plan as typeof plan & { subHeading?: string }).subHeading && (
                  <p className={`text-xs font-bold uppercase tracking-wide mt-1 mb-1 ${plan.highlight ? "text-[#0B2A3A]/60" : "text-white/55"}`}>
                    {(plan as typeof plan & { subHeading?: string }).subHeading}
                  </p>
                )}

                {/* Price */}
                <div className="mt-4 mb-4">
                  <span className={`font-extrabold leading-tight ${plan.price === "Custom Pricing" ? "text-xl" : "text-2xl"} ${plan.highlight ? "text-[#0B2A3A]" : "text-white"}`}>
                    {plan.price}
                  </span>
                  {plan.pricePer && (
                    <span className={`text-sm font-medium ml-1 ${plan.highlight ? "text-[#0B2A3A]/55" : "text-white/50"}`}>
                      {plan.pricePer}
                    </span>
                  )}
                </div>

                {/* Description */}
                <p className={`text-sm leading-relaxed mb-6 ${plan.highlight ? "text-[#0B2A3A]/70" : "text-white/55"}`}>
                  {plan.description}
                </p>

                {/* Divider */}
                <div className={`h-px mb-6 ${plan.highlight ? "bg-gray-200" : "bg-white/10"}`} />

                {/* Features */}
                <ul className="flex flex-col gap-3 flex-1 mb-6">
                  {plan.features.map((feat) => (
                    <li key={feat} className="flex items-start gap-3">
                      <span className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5 text-[#F1663A]">
                        <i className="ri-check-line text-sm font-bold" />
                      </span>
                      <span className={`text-sm leading-snug ${plan.highlight ? "text-[#0B2A3A]/80" : "text-white/70"}`}>
                        {feat}
                      </span>
                    </li>
                  ))}
                </ul>

                {/* ROI callout */}
                {plan.roi && (
                  <div className={`rounded-xl px-4 py-2.5 mb-6 flex items-center gap-2.5 ${plan.highlight ? "bg-[#F1663A]/10 border border-[#F1663A]/20" : "bg-[#F1663A]/10 border border-[#F1663A]/20"}`}>
                    <span className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                      <i className="ri-bar-chart-2-line text-[#F1663A] text-sm" />
                    </span>
                    <span className={`text-xs font-bold ${plan.highlight ? "text-[#0B2A3A]" : "text-white/90"}`}>
                      {plan.roi}
                    </span>
                  </div>
                )}

                {/* CTA */}
                <div className="mt-auto">
                  {plan.ctaVariant === "primary" ? (
                    <button
                      onClick={onCTAClick}
                      className="w-full py-3.5 rounded-xl font-bold text-sm bg-[#F1663A] text-white hover:bg-[#d9552a] transition-colors cursor-pointer whitespace-nowrap"
                      style={{ boxShadow: "0 4px 16px rgba(241,102,58,0.32)" }}
                    >
                      {plan.cta}
                    </button>
                  ) : (
                    <button
                      onClick={onCTAClick}
                      className="w-full py-3.5 rounded-xl font-bold text-sm border-2 border-white/30 text-white hover:border-[#F1663A] hover:text-[#F1663A] transition-colors cursor-pointer whitespace-nowrap"
                    >
                      {plan.cta}
                    </button>
                  )}

                  {plan.note && (
                    <p className={`text-xs text-center mt-3 leading-snug ${plan.highlight ? "text-[#0B2A3A]/45" : "text-white/35"}`}>
                      {plan.note}
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Why Timing Wins block */}
        <div className="mt-10 md:mt-16 max-w-6xl mx-auto">
          <div
            className="rounded-2xl border border-white/12 px-4 md:px-8 py-8 md:py-12"
            style={{ backgroundColor: "rgba(255,255,255,0.04)" }}
          >
            <div className="max-w-3xl mx-auto text-center mb-8 md:mb-10">
              <div className="inline-flex items-center gap-2 bg-[#F1663A]/15 border border-[#F1663A]/25 rounded-full px-4 py-1.5 mb-5">
                <i className="ri-timer-flash-line text-[#F1663A] text-xs" />
                <span className="text-xs font-semibold text-[#F1663A] tracking-wide uppercase">Timing Advantage</span>
              </div>
              <h4
                className="text-white font-bold mb-4"
                style={{ fontSize: "clamp(22px, 2.5vw, 32px)", lineHeight: "1.2" }}
              >
                Why Timing Wins
              </h4>
              <p className="text-white/60 text-sm leading-relaxed mb-2">
                Most outreach happens too late — after the listing is live, after the deal is already in motion.
              </p>
              <p className="text-white/60 text-sm leading-relaxed">
                <span className="text-white font-semibold">Imperium changes that.</span> With market-triggered outreach, you engage agents at the exact moment:
              </p>
            </div>

            {/* Trigger pills */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-8 md:mb-10">
              {triggers.map((t) => (
                <div
                  key={t.label}
                  className="flex items-center gap-3 w-full sm:w-auto bg-white/8 border border-white/15 rounded-xl px-4 py-3"
                  style={{ backgroundColor: "rgba(255,255,255,0.07)" }}
                >
                  <span className="w-8 h-8 flex items-center justify-center bg-[#F1663A]/15 rounded-lg flex-shrink-0">
                    <i className={`${t.icon} text-[#F1663A] text-base`} />
                  </span>
                  <span className="text-white/80 text-sm font-medium">{t.label}</span>
                </div>
              ))}
            </div>

            {/* Highlight line */}
            <div className="text-center">
              <p className="text-white font-bold text-base lg:text-lg">
                This is where the next closing is actually won.
              </p>
            </div>
          </div>
        </div>

        {/* Disclaimer */}
        <p className="text-center text-white/30 text-xs mt-8">
          Pricing varies based on market size, agent density, and transaction volume.
        </p>
      </div>
    </section>
  );
}
